import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ListChecks } from "lucide-react";
import { api } from "@/lib/api";

// Shows the current (first not-done) phase for a record that has phase-like
// children — i.e. related records carrying a `status` + `sequence`. Renders
// nothing for records without phases, so it's safe on any detail page.
export function CurrentPhaseBanner({ recordId }) {
  const [state, setState] = useState(undefined); // undefined=loading

  useEffect(() => {
    let alive = true;
    api.get(`/records/${recordId}/relationships`).then((r) => {
      if (!alive) return;
      const groups = r.data.groups || [];
      let items = [];
      for (const g of groups) {
        if ((g.items || []).some((it) => it.fields && it.fields.status !== undefined && it.fields.sequence !== undefined)) {
          items = g.items; break;
        }
      }
      if (!items.length) { setState(null); return; }
      const sorted = [...items].sort((a, b) => ((a.fields?.sequence ?? 999) - (b.fields?.sequence ?? 999)));
      const doneCount = items.filter((i) => ["Done", "Completed"].includes(i.fields?.status)).length;
      const current = sorted.find((it) => !["Done", "Completed"].includes(it.fields?.status));
      setState({ current, total: items.length, doneCount });
    }).catch(() => setState(null));
    return () => { alive = false; };
  }, [recordId]);

  if (!state) return null;
  const { current, total, doneCount } = state;

  if (!current) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
        <CheckCircle2 className="w-4 h-4" /> All {total} phases complete.
      </div>
    );
  }
  const f = current.fields || {};
  const cl = Array.isArray(f.checklist) ? f.checklist : [];
  const done = cl.filter((c) => c && c.done).length;
  return (
    <Link to={`/records/${current.id}`}
      className="mb-4 flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 hover:bg-primary/10 transition-colors"
      data-testid="current-phase-banner">
      <ListChecks className="w-4 h-4 text-primary shrink-0" />
      <div className="min-w-0 flex-1 text-sm">
        <span className="text-muted-foreground">Current phase: </span>
        <span className="font-semibold">{f.sequence != null ? `#${f.sequence} ` : ""}{current.title}</span>
        {cl.length > 0 && <span className="text-muted-foreground"> · checklist {done}/{cl.length}</span>}
        <span className="text-muted-foreground"> · {doneCount}/{total} phases done</span>
      </div>
      <ArrowRight className="w-4 h-4 text-primary shrink-0" />
    </Link>
  );
}
